import React, { FC, useState } from 'react';
import { useQuery } from '@apollo/client';
import { TextField, Box, Button, Grid, useMediaQuery, Theme } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { getLabsTemplate } from 'apollo/templates/user/query.graphql';
import { useTranslation } from 'react-i18next';
import { OrgDropdown } from 'models/user';
import FreeTextFilterComponent from 'components/users/shared/free-text-filter/free-text-filter.component';
import FilterOnStatusDropdown from 'components/users/shared/filter-on-status-dropdown/filter-on-status-dropdown.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { UserListFilter } from 'models/user';
import AddIcon from '@mui/icons-material/Add';

type AdminTechniciansPageActionsProps = {
    onFilterChange: (technicianFilter: UserListFilter) => void;
    onDialogOpenClick: () => void;
    onLabChange: (labId: string | null) => void;
};

const AdminTechniciansPageActions: FC<AdminTechniciansPageActionsProps> = ({
    onFilterChange,
    onDialogOpenClick,
    onLabChange,
}: AdminTechniciansPageActionsProps) => {
    const { t: translate } = useTranslation(['users']);
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
    const [technicianFilter, setTechnicianFilter] = useState<UserListFilter>({
        freeText: '',
        active: true,
    });
    const { showBoundary } = useDenthubErrorBoundary();

    const { data: { labs } = {}, loading } = useQuery(getLabsTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    const handleSelectLab = (_: React.SyntheticEvent, lab: OrgDropdown | null) => {
        if (lab) return onLabChange(lab._id);
        onLabChange(null);
    };

    const handleFilterChange = (filterValue: string) => {
        setTechnicianFilter({
            freeText: filterValue,
            active: technicianFilter.active,
        });
        onFilterChange({
            freeText: filterValue,
            active: technicianFilter.active,
        });
    };

    const handleStatusSelect = (filterStatus: boolean | null) => {
        setTechnicianFilter({
            freeText: technicianFilter.freeText,
            active: filterStatus,
        });
        onFilterChange({
            freeText: technicianFilter.freeText,
            active: filterStatus,
        });
    };

    return (
        <Box mb={1} display="flex" alignItems="center" flexDirection={!isMobile ? 'row' : 'column-reverse'}>
            <Grid container spacing={1}>
                <Grid item md={4} xs={12}>
                    <Autocomplete
                        fullWidth
                        id="labs"
                        disabled={loading}
                        options={labs || []}
                        onChange={handleSelectLab}
                        getOptionLabel={(option: OrgDropdown) => option.name}
                        renderInput={(params) => (
                            <TextField {...params} fullWidth margin="dense" label={translate('users:FILTER_LAB')} />
                        )}
                    />
                </Grid>
                <Grid item md={3} xs={12}>
                    <FreeTextFilterComponent
                        value={technicianFilter.freeText}
                        freeTextFilterChange={handleFilterChange}
                        label="FILTER_NAME"
                    />
                </Grid>
                <Grid item md={2} xs={12}>
                    <FilterOnStatusDropdown fullWidth statusSelected={handleStatusSelect} />
                </Grid>
            </Grid>
            <Box flex="1 0 auto">
                <Button variant="contained" onClick={onDialogOpenClick} startIcon={<AddIcon />}>
                    {translate('users:ADD_TECHNICIAN')}
                </Button>
            </Box>
        </Box>
    );
};

export default AdminTechniciansPageActions;
